import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowsPointingOutIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { api, loadBuildingsForSelector } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  needsBuildingSelection,
  shouldShowBuildingSelector,
  selectBuilding,
} from '../../utils/buildingSelection';
import { apartmentsApi } from '../../api/apartmentsApi';
import { menuApi } from '../../api/menuApi';
import {
  paymentApi,
  ReceivableActivityCategoryColumn,
  receivableFeeColumnsForDetailGrid,
  receivableFeeColumnsForIncurredSection,
  ReceivableFeeCategoryColumn,
  ReceivablePeriodDetailRow,
  ReceivablePeriodOverview,
  ReceivablePeriodReconciliation,
  ReceivablePeriodReconciliationBillSample,
} from '../../api/paymentApi';
import BillingPeriodMonthPicker from '../../components/common/BillingPeriodMonthPicker';
import {
  filterReceivableActivityColumnsWithAmounts,
  filterReceivableFeeColumnsAnyAmount,
  filterReceivableFeeColumnsWithAmounts,
  hasNonZeroLedgerAmount,
} from '../../utils/receivableStatisticsColumns';
import { getCurrentBillingPeriodValue } from '../../utils/billingPeriodPicker';
import { downloadExcel, sanitizeExportFilename } from '../../utils/excelExport';
import { UNASSIGNED_APARTMENT_GROUP_ID } from '../../constants/apartmentGroups';
import toast from 'react-hot-toast';

interface Building {
  id: string;
  name: string;
}

interface ApartmentGroupOption {
  id: string;
  name: string;
}

const formatMoney = (value: number) =>
  new Intl.NumberFormat('vi-VN').format(Math.round(value));

const formatMoneyCell = (value: number) => `${formatMoney(value)}đ`;

/** border-collapse breaks position:sticky on th in Chrome/Safari */
const DETAIL_TABLE_CLASS = 'min-w-max w-full border-separate border-spacing-0';
/** Sticky thead needs a scroll ancestor (same as Toàn màn hình / bảng ghép phiếu). */
const DETAIL_TABLE_SCROLL_CLASS =
  'max-h-[min(70vh,56rem)] overflow-auto overscroll-contain';
const DETAIL_TH = 'border border-gray-200 px-2 py-2 bg-gray-50';
const DETAIL_TH_LEFT = `${DETAIL_TH} text-left`;
const DETAIL_TH_SUB = `${DETAIL_TH} normal-case`;
const DETAIL_TH_ACCENT = 'border border-gray-200 px-2 py-2 bg-gray-100';
const DETAIL_TH_STICKY_ROW1 = `${DETAIL_TH} sticky top-0 z-40 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]`;
const DETAIL_TH_STICKY_ROW1_LEFT = `${DETAIL_TH_LEFT} sticky top-0 z-40 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]`;
const DETAIL_TH_STICKY_ROW2 = `${DETAIL_TH} sticky top-9 z-40`;
const DETAIL_TH_STICKY_SUB = `${DETAIL_TH_SUB} sticky top-9 z-40`;
const DETAIL_TH_STICKY_ACCENT =
  'border border-gray-200 px-2 py-2 bg-gray-100 sticky top-9 z-40';

function sumViiCashBillReconciliationRows(
  rows: ReceivablePeriodReconciliationBillSample[],
) {
  return rows.reduce(
    (acc, row) => ({
      transactionAmount: acc.transactionAmount + row.transactionAmount,
      cashToVii: acc.cashToVii + row.cashToVii,
      creditToVii: acc.creditToVii + row.creditToVii,
      paymentViiLedgerTotal:
        acc.paymentViiLedgerTotal + row.paymentViiLedgerTotal,
      cashShcdToVii: acc.cashShcdToVii + row.cashShcdToVii,
      extraBillCredit: acc.extraBillCredit + row.extraBillCredit,
      creditApplied: acc.creditApplied + row.creditApplied,
      surplusCreditToWallet:
        acc.surplusCreditToWallet + (row.overpaymentAmount ?? 0),
      cashOutsideVii:
        acc.cashOutsideVii +
        (row.cashOutsideVii ?? row.cashNotCountedInVii ?? 0),
      gapViiMinusTransaction:
        acc.gapViiMinusTransaction + row.gapViiMinusTransaction,
    }),
    {
      transactionAmount: 0,
      cashToVii: 0,
      creditToVii: 0,
      paymentViiLedgerTotal: 0,
      cashShcdToVii: 0,
      extraBillCredit: 0,
      creditApplied: 0,
      surplusCreditToWallet: 0,
      cashOutsideVii: 0,
      gapViiMinusTransaction: 0,
    },
  );
}

function formatWalletViiCell(row: ReceivablePeriodReconciliationBillSample) {
  const creditOut = row.creditApplied ?? 0;
  const walletIn = row.overpaymentAmount ?? 0;
  const hasOut = creditOut > 0;
  const hasIn = walletIn > 0;
  if (!hasOut && !hasIn) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <div className="leading-snug">
      {hasOut && (
        <div>
          <span className="text-red-700">− Trừ ví</span>{' '}
          {formatMoney(creditOut)}
        </div>
      )}
      {hasIn && (
        <div className="text-emerald-800">
          + Vào ví <span className="text-gray-500">(thu dư)</span> {formatMoney(walletIn)}
        </div>
      )}
      {hasOut && row.creditAllocatedOnNoticeVii > 0 && (
        <div className="text-[10px] text-gray-500">
          PB TB {formatMoney(row.creditAllocatedOnNoticeVii)}
          {row.extraBillCredit > 0 ? ` · thừa ${formatMoney(row.extraBillCredit)}` : ''}
        </div>
      )}
      {hasOut && row.creditAllocatedOnNoticeVii === 0 && row.extraBillCredit > 0 && (
        <div className="text-[10px] text-gray-500">thừa {formatMoney(row.extraBillCredit)}</div>
      )}
    </div>
  );
}

const OverviewCell: React.FC<{ value: number; highlight?: boolean }> = ({
  value,
  highlight,
}) => (
  <td
    className={`px-3 py-2 text-right text-sm border border-gray-200 ${
      highlight ? 'bg-gray-100 font-medium' : ''
    }`}
  >
    {formatMoneyCell(value)}
  </td>
);

const DetailMoneyCell: React.FC<{ value: number; highlight?: boolean }> = ({
  value,
  highlight,
}) => (
  <td
    className={`px-2 py-1.5 text-right text-xs border border-gray-200 whitespace-nowrap ${
      highlight ? 'bg-sky-50 font-semibold' : ''
    }`}
  >
    {formatMoney(value)}
  </td>
);

const renderFeeAmounts = (
  row: ReceivablePeriodDetailRow,
  feeCategoryColumns: ReceivableFeeCategoryColumn[],
  field: 'openingByFee' | 'incurredByFee' | 'paymentByFee',
  highlight = false,
) =>
  feeCategoryColumns.map((column) => (
    <DetailMoneyCell
      key={`${row.apartmentId}-${field}-${column.id}`}
      value={row[field]?.[column.id] || 0}
      highlight={highlight}
    />
  ));

const renderActivityAmounts = (
  row: ReceivablePeriodDetailRow,
  columns: ReceivableActivityCategoryColumn[],
  field: 'collectionActivities' | 'spendingActivities',
  highlight = false,
) =>
  columns.map((column) => (
    <DetailMoneyCell
      key={`${row.apartmentId}-${field}-${column.id}`}
      value={row[field][column.id] || 0}
      highlight={highlight}
    />
  ));

type StatisticsTabProps = {
  onDetailSheetFullscreenChange?: (open: boolean) => void;
};

export const StatisticsTab: React.FC<StatisticsTabProps> = ({
  onDetailSheetFullscreenChange,
}) => {
  const { user, isSuperAdmin, isOperator } = useAuth();
  const detailRef = useRef<HTMLDivElement>(null);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [groups, setGroups] = useState<ApartmentGroupOption[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [collectionPeriod, setCollectionPeriod] = useState(getCurrentBillingPeriodValue());
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [apartmentSearch, setApartmentSearch] = useState('');
  const [overview, setOverview] = useState<ReceivablePeriodOverview | null>(null);
  const [reconciliation, setReconciliation] = useState<ReceivablePeriodReconciliation | null>(null);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [showViiCashBillTable, setShowViiCashBillTable] = useState(false);
  const [feeCategoryColumns, setFeeCategoryColumns] = useState<ReceivableFeeCategoryColumn[]>([]);
  const [revenueActivityColumns, setRevenueActivityColumns] = useState<ReceivableActivityCategoryColumn[]>([]);
  const [expenditureActivityColumns, setExpenditureActivityColumns] = useState<ReceivableActivityCategoryColumn[]>([]);
  const [showRevenueActivities, setShowRevenueActivities] = useState(false);
  const [showExpenditureActivities, setShowExpenditureActivities] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const [generatedAt, setGeneratedAt] = useState('');
  const [detailTotals, setDetailTotals] = useState<ReceivablePeriodDetailRow | null>(null);
  const [detailItems, setDetailItems] = useState<ReceivablePeriodDetailRow[]>([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(1);
  const [detailLimit, setDetailLimit] = useState(100);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailSheetFullscreen, setDetailSheetFullscreen] = useState(false);

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    }).catch(console.error);
  }, [user]);

  useEffect(() => {
    if (!selectedBuildingId) {
      setGroups([]);
      setSelectedGroupIds([]);
      return;
    }
    apartmentsApi.getApartmentGroups(selectedBuildingId)
      .then((data) => setGroups(data || []))
      .catch(console.error);
  }, [selectedBuildingId]);

  useEffect(() => {
    if (isSuperAdmin || isOperator) {
      setShowRevenueActivities(true);
      setShowExpenditureActivities(true);
      return;
    }
    Promise.all([
      menuApi.checkFunctionAccess('IE_REVENUE_ACTIVITIES'),
      menuApi.checkFunctionAccess('IE_EXPENDITURE_ACTIVITIES'),
    ]).then(([revenueAccess, expenditureAccess]) => {
      setShowRevenueActivities(revenueAccess);
      setShowExpenditureActivities(expenditureAccess);
    }).catch(() => {
      setShowRevenueActivities(false);
      setShowExpenditureActivities(false);
    });
  }, [isSuperAdmin, isOperator]);

  const applyColumnMetadata = (data: {
    feeCategoryColumns: ReceivableFeeCategoryColumn[];
    revenueActivityColumns: ReceivableActivityCategoryColumn[];
    expenditureActivityColumns: ReceivableActivityCategoryColumn[];
  }) => {
    setFeeCategoryColumns(data.feeCategoryColumns || []);
    setRevenueActivityColumns(data.revenueActivityColumns || []);
    setExpenditureActivityColumns(data.expenditureActivityColumns || []);
  };

  const loadOverview = useCallback(async (refresh = false) => {
    if (!selectedBuildingId) {
      setOverview(null);
      setReconciliation(null);
      return;
    }
    setLoadingOverview(true);
    try {
      const data = await paymentApi.getReceivableStatisticsOverview(
        selectedBuildingId,
        collectionPeriod,
        refresh,
      );
      setOverview(data.overview);
      setReconciliation(data.reconciliation ?? null);
      applyColumnMetadata(data);
      setIsFrozen(data.isFrozen);
      setGeneratedAt(data.generatedAt);
    } catch {
      toast.error('Không thể tải thống kê kỳ phí');
    } finally {
      setLoadingOverview(false);
    }
  }, [selectedBuildingId, collectionPeriod]);

  const loadDetail = useCallback(async (refresh = false) => {
    if (!selectedBuildingId) {
      setDetailItems([]);
      setDetailTotals(null);
      return;
    }
    setLoadingDetail(true);
    try {
      const data = await paymentApi.getReceivableStatisticsDetail({
        buildingId: selectedBuildingId,
        collectionPeriod,
        apartmentGroupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        apartmentSearch,
        page: detailPage,
        limit: detailLimit,
        refresh,
      });
      setDetailTotals(data.totals);
      setDetailItems(data.items);
      setDetailTotal(data.total);
      applyColumnMetadata(data);
      setIsFrozen(data.isFrozen);
      setGeneratedAt(data.generatedAt);
    } catch {
      toast.error('Không thể tải báo cáo chi tiết');
    } finally {
      setLoadingDetail(false);
    }
  }, [
    selectedBuildingId,
    collectionPeriod,
    selectedGroupIds,
    apartmentSearch,
    detailPage,
    detailLimit,
  ]);

  useEffect(() => {
    if (!selectedBuildingId) return;
    loadOverview();
  }, [loadOverview, selectedBuildingId]);

  useEffect(() => {
    if (!selectedBuildingId) return;
    const timer = setTimeout(() => {
      loadDetail();
    }, 300);
    return () => clearTimeout(timer);
  }, [loadDetail, selectedBuildingId]);

  useEffect(() => {
    setDetailPage(1);
  }, [collectionPeriod, selectedGroupIds, apartmentSearch, selectedBuildingId]);

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
    );
  };

  const scrollToDetail = () => {
    detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openDetailSheetFullscreen = () => {
    setDetailSheetFullscreen(true);
  };

  const closeDetailSheetFullscreen = () => {
    setDetailSheetFullscreen(false);
  };

  useEffect(() => {
    onDetailSheetFullscreenChange?.(detailSheetFullscreen);
  }, [detailSheetFullscreen, onDetailSheetFullscreenChange]);

  useEffect(() => {
    if (!detailSheetFullscreen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDetailSheetFullscreen();
      }
    };
    const mainEl = document.querySelector('main');
    const previousBodyOverflow = document.body.style.overflow;
    const previousMainOverflow =
      mainEl instanceof HTMLElement ? mainEl.style.overflow : '';
    document.body.style.overflow = 'hidden';
    if (mainEl instanceof HTMLElement) {
      mainEl.style.overflow = 'hidden';
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (mainEl instanceof HTMLElement) {
        mainEl.style.overflow = previousMainOverflow;
      }
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [detailSheetFullscreen]);

  const selectedBuildingName =
    buildings.find((b) => b.id === selectedBuildingId)?.name || 'bao-cao';

  const incurredFeeCategoryColumns = useMemo(
    () => receivableFeeColumnsForIncurredSection(feeCategoryColumns),
    [feeCategoryColumns],
  );
  const detailFeeCategoryColumns = useMemo(
    () => receivableFeeColumnsForDetailGrid(feeCategoryColumns),
    [feeCategoryColumns],
  );
  const visibleOpeningFeeCategoryColumns = useMemo(
    () =>
      filterReceivableFeeColumnsWithAmounts(detailFeeCategoryColumns, detailTotals, {
        opening: true,
      }),
    [detailFeeCategoryColumns, detailTotals],
  );
  const visiblePaymentFeeCategoryColumns = useMemo(
    () =>
      filterReceivableFeeColumnsWithAmounts(detailFeeCategoryColumns, detailTotals, {
        payment: true,
      }),
    [detailFeeCategoryColumns, detailTotals],
  );
  const showOpeningDebitColumn = useMemo(
    () => !detailTotals || hasNonZeroLedgerAmount(detailTotals.openingDebit),
    [detailTotals],
  );
  const showOpeningCreditColumn = useMemo(
    () => !detailTotals || hasNonZeroLedgerAmount(detailTotals.openingCredit),
    [detailTotals],
  );
  const openingBalanceColumnCount = useMemo(
    () =>
      (showOpeningDebitColumn ? 1 : 0) +
      (showOpeningCreditColumn ? 1 : 0) +
      visibleOpeningFeeCategoryColumns.length,
    [
      showOpeningDebitColumn,
      showOpeningCreditColumn,
      visibleOpeningFeeCategoryColumns.length,
    ],
  );
  const visibleIncurredFeeCategoryColumns = useMemo(
    () =>
      filterReceivableFeeColumnsWithAmounts(incurredFeeCategoryColumns, detailTotals, {
        incurred: true,
      }),
    [incurredFeeCategoryColumns, detailTotals],
  );
  const visibleFeeColumnsForDescription = useMemo(
    () => filterReceivableFeeColumnsAnyAmount(detailFeeCategoryColumns, detailTotals),
    [detailFeeCategoryColumns, detailTotals],
  );
  const visibleRevenueColumns = useMemo(() => {
    if (!showRevenueActivities) return [];
    return filterReceivableActivityColumnsWithAmounts(
      revenueActivityColumns,
      detailTotals,
      'collectionActivities',
    );
  }, [showRevenueActivities, revenueActivityColumns, detailTotals]);
  const visibleExpenditureColumns = useMemo(() => {
    if (!showExpenditureActivities) return [];
    return filterReceivableActivityColumnsWithAmounts(
      expenditureActivityColumns,
      detailTotals,
      'spendingActivities',
    );
  }, [showExpenditureActivities, expenditureActivityColumns, detailTotals]);

  const viiCashBillColumnTotals = useMemo(() => {
    const rows = reconciliation?.viiCashBillReconciliation;
    if (!rows?.length) {
      return null;
    }
    return sumViiCashBillReconciliationRows(rows);
  }, [reconciliation?.viiCashBillReconciliation]);

  const detailColumnCount = useMemo(() => {
    const openingFeeCount = visibleOpeningFeeCategoryColumns.length;
    const paymentFeeCount = visiblePaymentFeeCategoryColumns.length;
    const openingAggregateCount =
      (showOpeningDebitColumn ? 1 : 0) + (showOpeningCreditColumn ? 1 : 0);
    const incurredFeeCount = visibleIncurredFeeCategoryColumns.length;
    return (
      3 +
      openingAggregateCount +
      openingFeeCount +
      incurredFeeCount +
      1 +
      3 +
      paymentFeeCount +
      1 +
      1 +
      2 +
      visibleRevenueColumns.length +
      visibleExpenditureColumns.length
    );
  }, [
    visibleOpeningFeeCategoryColumns.length,
    visiblePaymentFeeCategoryColumns.length,
    showOpeningDebitColumn,
    showOpeningCreditColumn,
    visibleIncurredFeeCategoryColumns.length,
    visibleRevenueColumns.length,
    visibleExpenditureColumns.length,
  ]);

  const createdDateLabel = generatedAt
    ? new Date(generatedAt).toLocaleDateString('vi-VN')
    : new Date().toLocaleDateString('vi-VN');

  const totalPages = Math.max(1, Math.ceil(detailTotal / detailLimit));

  const handleExport = async () => {
    if (!selectedBuildingId) {
      toast.error('Vui lòng chọn chung cư');
      return;
    }
    try {
      const data = await paymentApi.exportReceivableStatisticsDetail({
        buildingId: selectedBuildingId,
        collectionPeriod,
        apartmentGroupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        apartmentSearch,
      });
      const exportFeeColumns = receivableFeeColumnsForDetailGrid(data.feeCategoryColumns);
      const exportIncurredColumns = receivableFeeColumnsForIncurredSection(
        data.feeCategoryColumns,
      );
      const totalsForColumns = data.totals;
      const exportOpeningFeeColumns = filterReceivableFeeColumnsWithAmounts(
        exportFeeColumns,
        totalsForColumns,
        { opening: true },
      );
      const exportPaymentFeeColumns = filterReceivableFeeColumnsWithAmounts(
        exportFeeColumns,
        totalsForColumns,
        { payment: true },
      );
      const exportShowOpeningDebit =
        !totalsForColumns || hasNonZeroLedgerAmount(totalsForColumns.openingDebit);
      const exportShowOpeningCredit =
        !totalsForColumns || hasNonZeroLedgerAmount(totalsForColumns.openingCredit);
      const exportIncurredFeeColumns = filterReceivableFeeColumnsWithAmounts(
        exportIncurredColumns,
        totalsForColumns,
        { incurred: true },
      );
      const exportRevenueColumns = showRevenueActivities
        ? filterReceivableActivityColumnsWithAmounts(
            data.revenueActivityColumns,
            totalsForColumns,
            'collectionActivities',
          )
        : [];
      const exportExpenditureColumns = showExpenditureActivities
        ? filterReceivableActivityColumnsWithAmounts(
            data.expenditureActivityColumns,
            totalsForColumns,
            'spendingActivities',
          )
        : [];
      const headers = [
        'Mã căn hộ',
        'Tên căn hộ',
        'Tên chủ hộ',
        ...(exportShowOpeningDebit ? ['Nợ đầu kỳ'] : []),
        ...(exportShowOpeningCredit ? ['Có đầu kỳ'] : []),
        ...exportOpeningFeeColumns.map((column) => `Nợ đầu kỳ ${column.name}`),
        ...exportIncurredFeeColumns.map((column) => `Phát sinh ${column.name}`),
        'Tổng phát sinh',
        'Phải thu tháng này',
        'Thu trước',
        'Chi trước',
        ...exportPaymentFeeColumns.map((column) => `Thanh toán ${column.name}`),
        'Tổng thanh toán',
        'Cấn trừ',
        'Nợ cuối kỳ',
        'Có cuối kỳ',
        ...exportRevenueColumns.map((column) => column.name),
        ...exportExpenditureColumns.map((column) => column.name),
      ];
      const rows = data.items.map((row) => [
        row.apartmentCode,
        row.apartmentName || '',
        row.ownerName,
        ...(exportShowOpeningDebit ? [row.openingDebit] : []),
        ...(exportShowOpeningCredit ? [row.openingCredit] : []),
        ...exportOpeningFeeColumns.map((column) => row.openingByFee?.[column.id] || 0),
        ...exportIncurredFeeColumns.map((column) => row.incurredByFee[column.id] || 0),
        row.totalIncurred,
        row.receivableThisMonth,
        row.preCollection,
        row.preExpenditure,
        ...exportPaymentFeeColumns.map((column) => row.paymentByFee[column.id] || 0),
        row.totalPayment,
        row.offset,
        row.closingDebit,
        row.closingCredit,
        ...exportRevenueColumns.map((column) => row.collectionActivities[column.id] || 0),
        ...exportExpenditureColumns.map((column) => row.spendingActivities[column.id] || 0),
      ]);
      const filename = `${sanitizeExportFilename(
        `thong-ke-phai-thu_${selectedBuildingName}_${collectionPeriod.replace('/', '-')}`,
      )}.xlsx`;
      downloadExcel('Thống kê phí phải thu', headers, rows, filename);
      toast.success('Đã xuất báo cáo');
    } catch {
      toast.error('Không thể xuất báo cáo');
    }
  };

  const renderDetailRow = (
    row: ReceivablePeriodDetailRow,
    options?: { highlight?: boolean; label?: string },
  ) => (
    <tr className={options?.highlight ? 'bg-sky-50 font-semibold' : 'hover:bg-gray-50'}>
      {options?.label ? (
        <td colSpan={3} className="border border-gray-200 px-2 py-1.5 text-xs bg-sky-50">
          {options.label}
        </td>
      ) : (
        <>
          <td className="border border-gray-200 px-2 py-1.5 text-xs">{row.apartmentCode}</td>
          <td className="border border-gray-200 px-2 py-1.5 text-xs">{row.apartmentName || ''}</td>
          <td className="border border-gray-200 px-2 py-1.5 text-xs">{row.ownerName}</td>
        </>
      )}
      {showOpeningDebitColumn && (
        <DetailMoneyCell value={row.openingDebit} highlight={options?.highlight} />
      )}
      {showOpeningCreditColumn && (
        <DetailMoneyCell value={row.openingCredit} highlight={options?.highlight} />
      )}
      {renderFeeAmounts(row, visibleOpeningFeeCategoryColumns, 'openingByFee', options?.highlight)}
      {renderFeeAmounts(row, visibleIncurredFeeCategoryColumns, 'incurredByFee', options?.highlight)}
      <DetailMoneyCell value={row.totalIncurred} highlight={options?.highlight} />
      <DetailMoneyCell value={row.receivableThisMonth} highlight={options?.highlight} />
      <DetailMoneyCell value={row.preCollection} highlight={options?.highlight} />
      <DetailMoneyCell value={row.preExpenditure} highlight={options?.highlight} />
      {renderFeeAmounts(row, visiblePaymentFeeCategoryColumns, 'paymentByFee', options?.highlight)}
      <DetailMoneyCell value={row.totalPayment} highlight={options?.highlight} />
      <DetailMoneyCell value={row.offset} highlight={options?.highlight} />
      <DetailMoneyCell value={row.closingDebit} highlight={options?.highlight} />
      <DetailMoneyCell value={row.closingCredit} highlight={options?.highlight} />
      {renderActivityAmounts(row, visibleRevenueColumns, 'collectionActivities', options?.highlight)}
      {renderActivityAmounts(row, visibleExpenditureColumns, 'spendingActivities', options?.highlight)}
    </tr>
  );

  const renderDetailThead = () => (
    <thead>
      <tr className="text-[11px] uppercase text-gray-700">
        <th rowSpan={2} className={DETAIL_TH_STICKY_ROW1_LEFT}>Mã căn hộ</th>
        <th rowSpan={2} className={DETAIL_TH_STICKY_ROW1_LEFT}>Tên căn hộ</th>
        <th rowSpan={2} className={DETAIL_TH_STICKY_ROW1_LEFT}>Tên chủ hộ</th>
        <th colSpan={openingBalanceColumnCount} className={DETAIL_TH_STICKY_ROW1}>
          Số dư đầu kỳ
        </th>
        <th colSpan={visibleIncurredFeeCategoryColumns.length + 1} className={DETAIL_TH_STICKY_ROW1}>
          Phát sinh trong kỳ
        </th>
        <th rowSpan={2} className={DETAIL_TH_STICKY_ROW1}>Phải thu tháng này</th>
        <th rowSpan={2} className={DETAIL_TH_STICKY_ROW1}>Thu trước</th>
        <th rowSpan={2} className={DETAIL_TH_STICKY_ROW1}>Chi trước</th>
        <th colSpan={visiblePaymentFeeCategoryColumns.length + 1} className={DETAIL_TH_STICKY_ROW1}>
          Thanh toán
        </th>
        <th rowSpan={2} className={DETAIL_TH_STICKY_ROW1}>Cấn trừ</th>
        <th colSpan={2} className={DETAIL_TH_STICKY_ROW1}>Số dư cuối kỳ</th>
        {visibleRevenueColumns.length > 0 && (
          <th colSpan={visibleRevenueColumns.length} className={DETAIL_TH_STICKY_ROW1}>
            Hoạt động thu
          </th>
        )}
        {visibleExpenditureColumns.length > 0 && (
          <th colSpan={visibleExpenditureColumns.length} className={DETAIL_TH_STICKY_ROW1}>
            Hoạt động chi
          </th>
        )}
      </tr>
      <tr className="text-[11px] uppercase text-gray-700">
        {showOpeningDebitColumn && (
          <th className={DETAIL_TH_STICKY_ROW2}>Nợ</th>
        )}
        {showOpeningCreditColumn && (
          <th className={DETAIL_TH_STICKY_ROW2}>Có</th>
        )}
        {visibleOpeningFeeCategoryColumns.map((column) => (
          <th key={`open-${column.id}`} className={DETAIL_TH_STICKY_SUB}>{column.name}</th>
        ))}
        {visibleIncurredFeeCategoryColumns.map((column) => (
          <th key={`inc-${column.id}`} className={DETAIL_TH_STICKY_SUB}>{column.name}</th>
        ))}
        <th className={DETAIL_TH_STICKY_ACCENT}>Tổng phát sinh</th>
        {visiblePaymentFeeCategoryColumns.map((column) => (
          <th key={`pay-${column.id}`} className={DETAIL_TH_STICKY_SUB}>{column.name}</th>
        ))}
        <th
          className={DETAIL_TH_STICKY_ACCENT}
          title="Tiền mặt thu phí (VII). Không gồm trừ ví — xem Cấn trừ."
        >
          Tổng thanh toán
        </th>
        <th className={DETAIL_TH_STICKY_ROW2}>Nợ</th>
        <th className={DETAIL_TH_STICKY_ROW2}>Có</th>
        {visibleRevenueColumns.map((column) => (
          <th key={`rev-${column.id}`} className={DETAIL_TH_STICKY_SUB}>{column.name}</th>
        ))}
        {visibleExpenditureColumns.map((column) => (
          <th key={`exp-${column.id}`} className={DETAIL_TH_STICKY_SUB}>{column.name}</th>
        ))}
      </tr>
    </thead>
  );

  const renderDetailTbody = () => (
    <tbody>
      {loadingDetail && !detailTotals ? (
        <tr>
          <td colSpan={detailColumnCount} className="px-4 py-8 text-center text-gray-500">
            Đang tải...
          </td>
        </tr>
      ) : (
        <>
          {detailTotals && renderDetailRow(detailTotals, { highlight: true, label: 'Tổng cộng' })}
          {detailItems.map((row) => (
            <React.Fragment key={row.apartmentId}>{renderDetailRow(row)}</React.Fragment>
          ))}
          {!loadingDetail && detailItems.length === 0 && (
            <tr>
              <td colSpan={detailColumnCount} className="px-4 py-8 text-center text-gray-500">
                Không có dữ liệu
              </td>
            </tr>
          )}
        </>
      )}
    </tbody>
  );

  const renderDetailTable = (options?: { scrollInside?: boolean }) => (
    <div
      className={[
        'border border-gray-200 rounded-lg min-w-0',
        options?.scrollInside ? DETAIL_TABLE_SCROLL_CLASS : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <table className={DETAIL_TABLE_CLASS}>
        {renderDetailThead()}
        {renderDetailTbody()}
      </table>
    </div>
  );

  const renderDetailReportControls = () => (
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          {!detailSheetFullscreen && (
            <>
              <h2 className="text-lg font-bold text-gray-900 text-center md:text-left">
                Danh sách thanh toán nhắc nợ {collectionPeriod}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Ngày tạo/ Date created {createdDateLabel}
              </p>
            </>
          )}
          {detailSheetFullscreen && (
            <p className="text-sm text-gray-500">Ngày tạo/ Date created {createdDateLabel}</p>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3 ml-auto">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Block</label>
            <div className="flex flex-wrap gap-2 max-w-md">
              <label className="inline-flex items-center gap-1 text-xs border rounded-full px-2 py-1">
                <input
                  type="checkbox"
                  checked={selectedGroupIds.includes(UNASSIGNED_APARTMENT_GROUP_ID)}
                  onChange={() => toggleGroup(UNASSIGNED_APARTMENT_GROUP_ID)}
                />
                Chưa gắn nhóm
              </label>
              {groups.map((group) => (
                <label
                  key={group.id}
                  className="inline-flex items-center gap-1 text-xs border rounded-full px-2 py-1"
                >
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                  />
                  {group.name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Căn hộ</label>
            <input
              type="text"
              value={apartmentSearch}
              onChange={(e) => setApartmentSearch(e.target.value)}
              placeholder="Mã căn hộ..."
              className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
          >
            Xuất báo cáo
          </button>
          {!detailSheetFullscreen && (
            <button
              type="button"
              onClick={openDetailSheetFullscreen}
              title="Toàn màn hình"
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
            >
              <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden />
              Toàn màn hình
            </button>
          )}
        </div>
      </div>
  );

  const renderDetailReportDescription = () => (
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Mô tả chi tiết</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
          {visibleFeeColumnsForDescription.map((column) => (
            <div key={column.id} className="text-gray-700">
              {column.name}: {column.period}
            </div>
          ))}
        </div>
        {(selectedGroupIds.length > 0 || apartmentSearch.trim()) && (
          <p className="text-xs text-amber-700 mt-2">
            Tổng cộng bên dưới chỉ tính theo bộ lọc Block/Căn hộ. Thống kê kỳ phí phía trên luôn là toàn bộ chung cư.
          </p>
        )}
        <p className="text-xs text-red-600 italic mt-2">
          Số liệu thống kê được đồng bộ từ module thu chi theo phiếu thu/chi đã duyệt.
          Dữ liệu kỳ trước được cố định; chỉ kỳ hiện tại thay đổi theo hóa đơn và thanh toán mới.
        </p>
      </div>
  );

  const renderDetailPagination = () => (
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-gray-600">
        <span>Tổng {detailTotal}</span>
        <div className="flex items-center gap-2">
          <select
            value={detailLimit}
            onChange={(e) => {
              setDetailLimit(Number(e.target.value));
              setDetailPage(1);
            }}
            className="border border-gray-300 rounded px-2 py-1"
          >
            {[25, 50, 100, 200].map((size) => (
              <option key={size} value={size}>{size}/trang</option>
            ))}
          </select>
          <button
            type="button"
            disabled={detailPage <= 1}
            onClick={() => setDetailPage((p) => Math.max(1, p - 1))}
            className="px-2 py-1 border rounded disabled:opacity-40"
          >
            ‹
          </button>
          <span>{detailPage} / {totalPages}</span>
          <button
            type="button"
            disabled={detailPage >= totalPages}
            onClick={() => setDetailPage((p) => Math.min(totalPages, p + 1))}
            className="px-2 py-1 border rounded disabled:opacity-40"
          >
            ›
          </button>
        </div>
      </div>
  );

  const renderDetailFullscreenLayout = () => (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="flex-1 min-h-0 overflow-auto px-4 py-3 overscroll-contain">
        {renderDetailReportControls()}
        {renderDetailReportDescription()}
        {renderDetailTable()}
      </div>
      <div className="shrink-0 border-t border-gray-200 px-4 py-3 bg-white">
        {renderDetailPagination()}
      </div>
    </div>
  );

  const renderDetailReportInner = () => (
    <>
      {renderDetailReportControls()}
      {renderDetailReportDescription()}
      {renderDetailTable({ scrollInside: true })}
      {renderDetailPagination()}
    </>
  );

  return (
    <div className="space-y-6">
      {shouldShowBuildingSelector(buildings) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Chung cư</label>
          <select
            value={selectedBuildingId}
            onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
            disabled={!needsBuildingSelection(buildings)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          >
            <option value="">Chọn chung cư</option>
            {buildings.map((building) => (
              <option key={building.id} value={building.id}>{building.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
            Thống kê kỳ phí
          </h2>
          <BillingPeriodMonthPicker
            value={collectionPeriod}
            onChange={setCollectionPeriod}
          />
          <button
            type="button"
            onClick={() => {
              loadOverview(true);
              loadDetail(true);
            }}
            disabled={loadingOverview || !selectedBuildingId}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <span className="text-emerald-600">↻</span>
            Làm mới dữ liệu
          </button>
          {isFrozen && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
              Dữ liệu cố định
            </span>
          )}
        </div>

        {loadingOverview && !overview ? (
          <div className="py-10 text-center text-gray-500">Đang tải...</div>
        ) : overview ? (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-center">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-700">
                  <th colSpan={2} className="border border-gray-200 px-2 py-2">Số dư đầu kỳ</th>
                  <th colSpan={8} className="border border-gray-200 px-2 py-2">Phát sinh trong kỳ</th>
                  <th colSpan={2} className="border border-gray-200 px-2 py-2">Số dư cuối kỳ</th>
                </tr>
                <tr className="bg-gray-50 text-xs text-gray-700">
                  <th className="border border-gray-200 px-2 py-2">Nợ</th>
                  <th className="border border-gray-200 px-2 py-2">Có</th>
                  <th colSpan={4} className="border border-gray-200 px-2 py-2">Nợ</th>
                  <th colSpan={4} className="border border-gray-200 px-2 py-2">Có</th>
                  <th className="border border-gray-200 px-2 py-2">Nợ</th>
                  <th className="border border-gray-200 px-2 py-2">Có</th>
                </tr>
                <tr className="bg-gray-50 text-xs text-gray-700">
                  <th className="border border-gray-200 px-2 py-2" />
                  <th className="border border-gray-200 px-2 py-2" />
                  <th className="border border-gray-200 px-2 py-2">Phát sinh</th>
                  <th className="border border-gray-200 px-2 py-2">Thuế GTGT</th>
                  <th className="border border-gray-200 px-2 py-2">Giảm trừ</th>
                  <th className="border border-gray-200 px-2 py-2 bg-gray-100">Tổng cộng</th>
                  <th
                    className="border border-gray-200 px-2 py-2 bg-gray-100"
                    title="VII — tiền mặt thu phí trong kỳ (≈ Báo cáo giao dịch). Không gồm cấn trừ ví (X)."
                  >
                    Thanh toán
                  </th>
                  <th className="border border-gray-200 px-2 py-2">Thu trước</th>
                  <th className="border border-gray-200 px-2 py-2">Chi trước</th>
                  <th className="border border-gray-200 px-2 py-2 bg-gray-100">Cấn trừ</th>
                  <th className="border border-gray-200 px-2 py-2" />
                  <th className="border border-gray-200 px-2 py-2" />
                </tr>
                <tr className="text-[11px] text-gray-500">
                  <th className="border border-gray-200 px-2 py-1">I</th>
                  <th className="border border-gray-200 px-2 py-1">II</th>
                  <th className="border border-gray-200 px-2 py-1">III</th>
                  <th className="border border-gray-200 px-2 py-1">IV</th>
                  <th className="border border-gray-200 px-2 py-1">V</th>
                  <th className="border border-gray-200 px-2 py-1 bg-gray-100">VI = III + IV - V</th>
                  <th
                    className="border border-gray-200 px-2 py-1 bg-gray-100"
                    title="Tiền mặt thu phí trong kỳ (≈ Báo cáo giao dịch). Không gồm cấn trừ ví (X)."
                  >
                    VII
                  </th>
                  <th className="border border-gray-200 px-2 py-1">VIII</th>
                  <th className="border border-gray-200 px-2 py-1">IX</th>
                  <th className="border border-gray-200 px-2 py-1 bg-gray-100">X</th>
                  <th
                    className="border border-gray-200 px-2 py-1 bg-gray-100"
                    title="Tổng Nợ cuối kỳ: cộng theo từng căn I+VI−VII−X (có điều chỉnh TB kỳ)"
                  >
                    XI (I+VI−VII−X)
                  </th>
                  <th className="border border-gray-200 px-2 py-1 bg-gray-100">XII = II + VIII - IX - X</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <OverviewCell value={overview.openingDebit} highlight />
                  <OverviewCell value={overview.openingCredit} />
                  <OverviewCell value={overview.incurred} />
                  <OverviewCell value={overview.vat} />
                  <OverviewCell value={overview.deduction} />
                  <OverviewCell value={overview.totalIncurred} highlight />
                  <OverviewCell value={overview.payment} highlight />
                  <OverviewCell value={overview.preCollection} />
                  <OverviewCell value={overview.preExpenditure} />
                  <OverviewCell value={overview.offset} highlight />
                  <OverviewCell value={overview.closingDebit} highlight />
                  <OverviewCell value={overview.closingCredit} highlight />
                </tr>
              </tbody>
            </table>
            {reconciliation && (
              <div className="mt-4 border border-cyan-100 rounded-lg bg-cyan-50/40 px-3 py-3 text-xs text-gray-700">
                <p className="font-semibold text-gray-900 mb-2">Đối chiếu kế toán (cùng kỳ)</p>
                <p className="mb-3 rounded-md border border-emerald-200 bg-emerald-50/90 px-3 py-2.5 text-sm text-gray-900">
                  <strong>Tiền mặt:</strong> Báo cáo giao dịch ≈ <strong>VII</strong> (Thanh toán phí — không gồm cấn
                  trừ ví <strong>X</strong>). <strong>Ví căn hộ:</strong> <strong>XII = II + VIII − IX − X</strong> (Có đầu kỳ gồm thu dư nhập
                  công nợ; Thu trước = ví vào trong kỳ; Cấn trừ = trừ ví khi thu phí).
                  {reconciliation.transactionReportPaidAtRangeLabel && (
                    <span className="block text-xs text-gray-600 mt-1">
                      GD: paidAt {reconciliation.transactionReportPaidAtRangeLabel}.
                    </span>
                  )}
                </p>
                {reconciliation.walletSurplusBreakdown && overview && (
                  <table className="min-w-full border border-gray-200 bg-white mb-3 text-xs">
                    <thead>
                      <tr className="bg-amber-50/90">
                        <th className="border border-gray-200 px-2 py-1 text-left">Tài khoản thu dư (Có)</th>
                        <th className="border border-gray-200 px-2 py-1 text-right">Số tiền</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-gray-200 px-2 py-1">II Có đầu kỳ (tổng)</td>
                        <td className="border border-gray-200 px-2 py-1 text-right font-medium">
                          {formatMoneyCell(overview.openingCredit)}
                        </td>
                      </tr>
                      {(reconciliation.walletSurplusBreakdown.openingCreditHandoverImport ?? 0) > 0 && (
                        <tr>
                          <td className="border border-gray-200 px-2 py-1 pl-4 text-gray-600">
                            ↳ Thu dư nhập file công nợ (THU DƯ)
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {formatMoneyCell(
                              reconciliation.walletSurplusBreakdown.openingCreditHandoverImport,
                            )}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="border border-gray-200 px-2 py-1">+ VIII Thu trước (vận hành)</td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {formatMoneyCell(reconciliation.walletSurplusBreakdown.preCollectionOperational)}
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-gray-200 px-2 py-1">− IX Chi trước</td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {formatMoneyCell(overview.preExpenditure)}
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-gray-200 px-2 py-1">− X Cấn trừ (trừ ví thu phí)</td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {formatMoneyCell(reconciliation.offsetX)}
                        </td>
                      </tr>
                      <tr className="bg-gray-50 font-medium">
                        <td className="border border-gray-200 px-2 py-1">= XII Có cuối kỳ</td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {formatMoneyCell(overview.closingCredit)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <p className="text-gray-500 mb-2 text-[11px]">
                  <strong>XI</strong> = tổng Nợ cuối kỳ (theo căn, thông báo kỳ); so với tab <strong>Công nợ</strong> khi
                  cùng bộ hóa đơn. Mở rộng bên dưới để ghép từng phiếu thu với GD.
                </p>
                <table className="min-w-full border border-gray-200 bg-white">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-200 px-2 py-1">XI Nợ cuối</th>
                      <th className="border border-gray-200 px-2 py-1">Công nợ (tab)</th>
                      <th className="border border-gray-200 px-2 py-1">VII</th>
                      <th className="border border-gray-200 px-2 py-1">X</th>
                      <th className="border border-gray-200 px-2 py-1">Báo cáo GD</th>
                      <th className="border border-gray-200 px-2 py-1">Δ GD−VII</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-2 py-1 text-right font-medium">
                        {formatMoneyCell(reconciliation.statisticsClosingDebitXi)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right font-medium">
                        {formatMoneyCell(reconciliation.receivableDebtTabRemaining)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right">
                        {formatMoneyCell(reconciliation.statisticsPaymentVii)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right">
                        {formatMoneyCell(reconciliation.offsetX)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right">
                        {formatMoneyCell(reconciliation.transactionReportCashTotal)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right font-medium text-cyan-900">
                        {formatMoneyCell(
                          reconciliation.transactionReportCashTotal -
                            reconciliation.statisticsPaymentVii,
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            {reconciliation && (
              <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowReconciliation((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-800 bg-gray-50 hover:bg-gray-100"
                >
                  <span>Đối chiếu với Báo cáo giao dịch &amp; Công nợ</span>
                  <span className="text-gray-500 text-xs">{showReconciliation ? 'Thu gọn' : 'Mở rộng'}</span>
                </button>
                {showReconciliation && (
                  <div className="px-4 py-4 space-y-6 text-sm text-gray-700">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Tiền mặt: VII ≈ Báo cáo giao dịch</h3>
                      <table className="min-w-full text-xs border border-gray-200 mb-2">
                        <tbody>
                          <tr>
                            <td className="border border-gray-200 px-2 py-1">VII (Thanh toán phí — tiền mặt)</td>
                            <td className="border border-gray-200 px-2 py-1 text-right">
                              {formatMoneyCell(reconciliation.statisticsPaymentVii)}
                            </td>
                          </tr>
                          <tr>
                            <td className="border border-gray-200 px-2 py-1">Báo cáo giao dịch</td>
                            <td className="border border-gray-200 px-2 py-1 text-right">
                              {formatMoneyCell(reconciliation.transactionReportCashTotal)}
                            </td>
                          </tr>
                          <tr className="bg-emerald-50">
                            <td className="border border-gray-200 px-2 py-1">Δ GD−VII (nên ≈ 0)</td>
                            <td className="border border-gray-200 px-2 py-1 text-right font-semibold">
                              {formatMoneyCell(
                                reconciliation.transactionReportCashTotal -
                                  reconciliation.statisticsPaymentVii,
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="border border-gray-200 px-2 py-1 text-gray-600">X (Cấn trừ ví — không trong VII)</td>
                            <td className="border border-gray-200 px-2 py-1 text-right text-gray-600">
                              {formatMoneyCell(reconciliation.offsetX)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      {reconciliation.viiCashMatch && (
                        <>
                          <h3 className="font-semibold text-gray-900 mb-2 mt-4">
                            Phân bổ VII (theo loại phí — không gồm cột ví riêng)
                          </h3>
                          <table className="min-w-full text-xs border border-gray-200 mb-2">
                            <tbody>
                              <tr>
                                <td className="border border-gray-200 px-2 py-1">Tiền mặt HĐ TB kỳ</td>
                                <td className="border border-gray-200 px-2 py-1 text-right">
                                  {formatMoneyCell(reconciliation.viiCashMatch.sumCashAllocatedOnNoticeVii)}
                                </td>
                              </tr>
                              {(reconciliation.viiCashMatch.sumCashPhongShcdVii ?? 0) > 0 && (
                                <tr>
                                  <td className="border border-gray-200 px-2 py-1">+ Tiền mặt Phí sử dụng phòng SHCĐ</td>
                                  <td className="border border-gray-200 px-2 py-1 text-right">
                                    {formatMoneyCell(reconciliation.viiCashMatch.sumCashPhongShcdVii)}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td className="border border-gray-200 px-2 py-1">
                                  + Trừ ví (creditAllocated trên HĐ; tổng trừ ví ≈ X)
                                </td>
                                <td className="border border-gray-200 px-2 py-1 text-right">
                                  {formatMoneyCell(reconciliation.viiCashMatch.sumCreditAllocatedOnNoticeVii)}
                                </td>
                              </tr>
                              <tr className="bg-gray-50 font-medium">
                                <td className="border border-gray-200 px-2 py-1">= VII (tổng)</td>
                                <td className="border border-gray-200 px-2 py-1 text-right">
                                  {formatMoneyCell(reconciliation.viiCashMatch.statisticsPaymentVii)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </>
                      )}
                      {reconciliation.walletSurplusBreakdown && overview && (
                        <>
                          <h3 className="font-semibold text-gray-900 mb-2 mt-4">Chi tiết VIII Thu trước</h3>
                          <table className="min-w-full text-xs border border-gray-200 mb-2">
                            <tbody>
                              <tr>
                                <td className="border border-gray-200 px-2 py-1">Thu dư phiếu phí (+ Vào ví)</td>
                                <td className="border border-gray-200 px-2 py-1 text-right">
                                  {formatMoneyCell(
                                    reconciliation.walletSurplusBreakdown.preCollectionBillOverpayment,
                                  )}
                                </td>
                              </tr>
                              {(reconciliation.walletSurplusBreakdown.preCollectionOther ?? 0) > 0 && (
                                <tr>
                                  <td className="border border-gray-200 px-2 py-1">Ví vào khác (CK, thủ công…)</td>
                                  <td className="border border-gray-200 px-2 py-1 text-right">
                                    {formatMoneyCell(reconciliation.walletSurplusBreakdown.preCollectionOther)}
                                  </td>
                                </tr>
                              )}
                              <tr className="bg-gray-50 font-medium">
                                <td className="border border-gray-200 px-2 py-1">VIII Thu trước (vận hành)</td>
                                <td className="border border-gray-200 px-2 py-1 text-right">
                                  {formatMoneyCell(
                                    reconciliation.walletSurplusBreakdown.preCollectionOperational,
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </>
                      )}
                      {(reconciliation.allocationsOutsideNoticeSet ?? 0) > 0 && (
                        <p className="text-xs text-gray-500">
                          Phân bổ VII ra ngoài bộ TB kỳ:{' '}
                          {formatMoney(reconciliation.allocationsOutsideNoticeSet)}đ
                        </p>
                      )}
                      {(reconciliation.viiCashBillReconciliation?.length ?? 0) > 0 && (
                        <div className="mb-4">
                          <button
                            type="button"
                            onClick={() => setShowViiCashBillTable((v) => !v)}
                            className="text-xs text-cyan-800 hover:underline"
                          >
                            {showViiCashBillTable ? 'Thu gọn' : 'Xem'} bảng ghép từng phiếu thu (
                            {reconciliation.viiCashBillReconciliation!.length})
                          </button>
                          {showViiCashBillTable && (
                            <div className="mt-2 max-h-80 overflow-auto border border-gray-200">
                              <table className="min-w-full text-xs">
                                <thead className="bg-gray-100 sticky top-0">
                                  <tr>
                                    <th className="border border-gray-200 px-2 py-1 text-left">Phiếu</th>
                                    <th className="border border-gray-200 px-2 py-1 text-right">GD</th>
                                    <th
                                      className="border border-gray-200 px-2 py-1 text-right"
                                      title="Tiền mặt vào VII: cashAllocated TB + Phí sử dụng phòng SHCĐ + overpayment"
                                    >
                                      TM→VII
                                    </th>
                                    <th
                                      className="border border-gray-200 px-2 py-1 text-right"
                                      title="Trừ ví = creditApplied (→ X, không VII). + Vào ví = overpayment (thu dư, VIII)."
                                    >
                                      Ví (ra → VII / vào)
                                    </th>
                                    <th className="border border-gray-200 px-2 py-1 text-right">VII</th>
                                    <th className="border border-gray-200 px-2 py-1 text-right">VII−GD</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {reconciliation.viiCashBillReconciliation!.map((row) => (
                                    <tr key={row.billId}>
                                      <td className="border border-gray-200 px-2 py-1">
                                        {row.receiptNumber ?? row.billId.slice(0, 8)}
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right">
                                        {formatMoneyCell(row.transactionAmount)}
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right">
                                        {formatMoneyCell(row.cashToVii)}
                                        {row.cashShcdToVii > 0 && (
                                          <span className="block text-[10px] text-gray-500">
                                            SHCĐ {formatMoney(row.cashShcdToVii)}
                                          </span>
                                        )}
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right align-top">
                                        {formatWalletViiCell(row)}
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right font-medium">
                                        {formatMoneyCell(row.paymentViiLedgerTotal)}
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right">
                                        {formatMoneyCell(row.gapViiMinusTransaction)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                {viiCashBillColumnTotals && (
                                  <tfoot className="bg-gray-100 font-semibold sticky bottom-0">
                                    <tr>
                                      <td className="border border-gray-200 px-2 py-1">
                                        Tổng ({reconciliation.viiCashBillReconciliation!.length}{' '}
                                        phiếu)
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right">
                                        {formatMoneyCell(viiCashBillColumnTotals.transactionAmount)}
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right">
                                        {formatMoneyCell(viiCashBillColumnTotals.cashToVii)}
                                        {viiCashBillColumnTotals.cashShcdToVii > 0 && (
                                          <span className="block text-[10px] text-gray-500 font-normal">
                                            SHCĐ {formatMoney(viiCashBillColumnTotals.cashShcdToVii)}
                                          </span>
                                        )}
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right align-top">
                                        <div className="leading-snug">
                                          {viiCashBillColumnTotals.creditApplied > 0 && (
                                            <div>
                                              <span className="text-red-800">− Trừ ví</span>{' '}
                                              {formatMoney(viiCashBillColumnTotals.creditApplied)}
                                            </div>
                                          )}
                                          {viiCashBillColumnTotals.surplusCreditToWallet > 0 && (
                                            <div className="text-emerald-900 font-normal">
                                              + Vào ví {formatMoney(viiCashBillColumnTotals.surplusCreditToWallet)}
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right">
                                        {formatMoneyCell(
                                          viiCashBillColumnTotals.paymentViiLedgerTotal,
                                        )}
                                      </td>
                                      <td className="border border-gray-200 px-2 py-1 text-right">
                                        {formatMoneyCell(
                                          viiCashBillColumnTotals.gapViiMinusTransaction,
                                        )}
                                      </td>
                                    </tr>
                                  </tfoot>
                                )}
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">Chọn chung cư để xem thống kê</div>
        )}

        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={scrollToDetail}
            className="text-sm text-cyan-700 hover:text-cyan-900"
          >
            Xem báo cáo →
          </button>
        </div>
      </div>

      {detailSheetFullscreen &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] flex flex-col bg-gray-100"
            role="dialog"
            aria-modal="true"
            aria-label={`Báo cáo phải thu ${collectionPeriod}`}
          >
            <div className="shrink-0 flex items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-2.5 shadow-sm">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  Danh sách thanh toán nhắc nợ {collectionPeriod}
                </p>
                <p className="text-xs text-gray-500">
                  Cuộn để xem dữ liệu — chỉ giữ hàng tiêu đề bảng. ESC để thoát.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDetailSheetFullscreen}
                className="inline-flex items-center gap-2 shrink-0 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <XMarkIcon className="h-4 w-4" aria-hidden />
                Thu nhỏ
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden p-3 sm:p-4">
              <div className="h-full min-h-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                {renderDetailFullscreenLayout()}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {!detailSheetFullscreen && (
        <div ref={detailRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          {renderDetailReportInner()}
        </div>
      )}
    </div>
  );
};


export default StatisticsTab;
